import { DashboardCourseView } from "@/components/course/DashboardCourseView";
import { SimpleCourseView } from "@/components/course/SimpleCourseView";
import { ErrorDisplay } from "@/components/ui";
import { useCourse } from "@/hooks/useCourse";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { useAuth } from "@/lib/AuthContext";
import { CourseStatus, CourseType } from "@/lib/constants";
import {
  courseHiddenFromDraftViewers,
  useDraftViewerGate,
} from "@/hooks/useDraftViewer";
import { logger } from "@/lib/logger";
import { useCurrentUrl } from "@/lib/useCurrentPath";
import { gaService } from "@/services/ga.service";
import { mixpanelService } from "@/services/mixpanel.service";
import {
  Redirect,
  useFocusEffect,
  useLocalSearchParams,
  useNavigation,
  usePathname,
} from "expo-router";
import React, { useCallback, useEffect, useRef } from "react";
import { CoursePagePremiumLoader } from "@/components/course/CoursePagePremiumLoader";
import { Platform, RefreshControl } from "react-native";
import { ScrollView, YStack } from "tamagui";

export default function CoursesScreen() {
  const { user } = useAuth();
  const navigation = useNavigation();
  const currentPath = usePathname();
  const { fullPath } = useCurrentUrl();

  const {
    "course-slug": courseSlug,
    courseId,
  } = useLocalSearchParams<{
    "course-slug": string;
    courseId?: string;
  }>();

  // Replaces the old `?min_status=1` URL trick: who may see non-published
  // content is derived from the signed-in user's draft_viewers allowlist
  // membership, never from the URL. Allowlisted -> Retired(0) (everything);
  // everyone else -> Published(3) (today's default).
  const { canViewDrafts, isReady: draftAccessReady } = useDraftViewerGate();

  const courseQuery = useCourse(courseSlug);
  const course = courseQuery.data;

  // hide_from_draft_viewers negates the allowlist for this specific course:
  // flagged courses behave as if the user were a normal (non-viewer) user.
  const courseHidden = courseHiddenFromDraftViewers(course?.metadata);
  const effectiveCanViewDrafts = canViewDrafts && !courseHidden;
  const minStatus = effectiveCanViewDrafts
    ? CourseStatus.Retired
    : CourseStatus.Published;

  useDocumentTitle(course?.title);

  useFocusEffect(
    useCallback(() => {
      navigation.setOptions({ title: "" });
    }, [navigation]),
  );

  useEffect(() => {
    if (courseQuery.error) {
      logger.error("Failed to load course:", {
        courseSlug,
        userId: user?.id,
        route: currentPath,
        error: courseQuery.error,
      });
    }
  }, [courseQuery.error, courseSlug, user?.id, currentPath]);

  // Fire once per visit, but only after the course query settles so the event
  // carries the human-readable Course Title (slug-only if the load failed).
  const courseVisitTracked = useRef(false);
  useEffect(() => {
    if (!courseSlug || courseVisitTracked.current) return;
    if (courseQuery.isLoading) return;
    courseVisitTracked.current = true;
    mixpanelService.trackCourseLandingVisit(
      courseSlug,
      user,
      fullPath,
      course?.title,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseSlug, courseQuery.isLoading, course?.title, user?.id]);

  useEffect(() => {
    if (!courseSlug || !course || Platform.OS !== "web") return;
    gaService.trackCourseView(courseSlug, course.title, user);
  }, [courseSlug, course, user?.id]);

  if (courseQuery.isLoading) {
    return (
      <YStack f={1} bg="$bg">
        <ScrollView
          contentContainerStyle={{
            flexGrow: 1,
            justifyContent: "center",
            paddingBottom: Platform.OS === "web" ? 0 : 16,
          }}
          showsVerticalScrollIndicator={Platform.OS === "web"}
          refreshControl={
            <RefreshControl
              refreshing={courseQuery.isRefetching}
              onRefresh={() => void courseQuery.refetch()}
            />
          }
        >
          <CoursePagePremiumLoader variant="course" />
        </ScrollView>
      </YStack>
    );
  }

  if (courseQuery.error || !course) {
    return (
      <YStack f={1} bg="$bg">
        <ErrorDisplay
          error={courseQuery.error ?? new Error("Course not found")}
          title="Course not found"
          message="This course link is invalid or no longer available."
        />
      </YStack>
    );
  }

  // Redirect-type courses (e.g. The Harmony Games) are stubs whose content
  // lives under a canonical course — follow redirect_url before the draft
  // bounce below, since these stubs stay unpublished on purpose.
  if (course.type === CourseType.Redirect && course.redirect_url) {
    return <Redirect href={course.redirect_url as any} />;
  }

  if (course.status !== CourseStatus.Published && !draftAccessReady) {
    return (
      <YStack f={1} bg="$bg" jc="center" ai="center">
        <CoursePagePremiumLoader variant="course" />
      </YStack>
    );
  }

  if (course.status !== CourseStatus.Published && !effectiveCanViewDrafts) {
    return <Redirect href="/courses" />;
  }

  if (course.type === CourseType.Simple) {
    return (
      <SimpleCourseView
        course={course}
        courseSlug={courseSlug}
        courseId={courseId}
        minStatus={minStatus}
      />
    );
  }

  return (
    <DashboardCourseView
      course={course}
      courseSlug={courseSlug}
      courseId={courseId}
      minStatus={minStatus}
    />
  );
}
